import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { harborApi } from '../api'
import type { OutdatedServer } from '../types'

interface TagsModalState {
  imageId: string
  imageName: string
}

export default function Harbor() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ repository: '', imageName: '' })
  const [tagsModal, setTagsModal] = useState<TagsModalState | null>(null)
  const [confirmTag, setConfirmTag] = useState<string | null>(null)

  const { data: images = [] } = useQuery({
    queryKey: ['harbor-images'],
    queryFn: () => harborApi.getImages().then(r => r.data),
  })

  const { data: outdated = [] } = useQuery({
    queryKey: ['harbor-outdated'],
    queryFn: () => harborApi.getOutdated().then(r => r.data as OutdatedServer[]),
  })

  const { data: availableTags = [], isFetching: fetchingTags } = useQuery({
    queryKey: ['harbor-tags', tagsModal?.imageId],
    queryFn: () => harborApi.getImageTags(tagsModal!.imageId).then(r => r.data),
    enabled: !!tagsModal,
  })

  const registerMutation = useMutation({
    mutationFn: () => harborApi.register(form.repository, form.imageName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harbor-images'] })
      setShowForm(false)
      setForm({ repository: '', imageName: '' })
    },
  })

  const syncMutation = useMutation({
    mutationFn: () => harborApi.sync(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harbor-images'] })
      qc.invalidateQueries({ queryKey: ['harbor-outdated'] })
    },
  })

  useEffect(() => { syncMutation.mutate() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const retargetMutation = useMutation({
    mutationFn: (targetTag: string) =>
      harborApi.retargetLatest(tagsModal!.imageId, targetTag),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harbor-images'] })
      qc.invalidateQueries({ queryKey: ['harbor-tags', tagsModal?.imageId] })
      qc.invalidateQueries({ queryKey: ['harbor-outdated'] })
      setConfirmTag(null)
    },
    onError: () => setConfirmTag(null),
  })

return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">Harbor 이미지 관리</h2>
        <div className="flex gap-2">
          <button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}
            className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg text-sm hover:bg-blue-50 transition disabled:opacity-50">
            {syncMutation.isPending ? '동기화 중...' : '⟳ Harbor 동기화'}
          </button>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition">
            + 이미지 등록
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-surface rounded-xl border p-5 mb-6 shadow-sm">
          <h3 className="font-semibold text-gray-700 mb-4">이미지 등록</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Repository</label>
              <input value={form.repository} onChange={e => setForm({ ...form, repository: e.target.value })}
                placeholder="예: myproject" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Image Name</label>
              <input value={form.imageName} onChange={e => setForm({ ...form, imageName: e.target.value })}
                placeholder="예: edge-app" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => registerMutation.mutate()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition">등록</button>
            <button onClick={() => setShowForm(false)}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 관리 중인 이미지 목록 */}
        <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50">
            <h3 className="font-semibold text-gray-700 text-sm">관리 이미지 목록</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Repository/Image</th>
                <th className="text-left px-4 py-2">latest 태그</th>
                <th className="text-left px-4 py-2">마지막 동기화</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {images.map(img => (
                <tr key={img.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{img.repository}/{img.imageName}</td>
                  <td className="px-4 py-3 text-xs">
                    {img.latestTag
                      ? <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-mono">{img.latestTag}</span>
                      : <span className="text-gray-400">미확인</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {img.lastSyncedAt ? new Date(img.lastSyncedAt).toLocaleString('ko-KR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-right">
                    <button onClick={() => setTagsModal({ imageId: img.id, imageName: img.imageName })}
                      className="text-blue-600 hover:underline">
                      버전 목록
                    </button>
                  </td>
                </tr>
              ))}
              {images.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">등록된 이미지가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 업데이트 필요 서버 */}
        <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-700 text-sm">업데이트 필요 서버</h3>
            {outdated.length > 0 && (
              <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full">{outdated.length}개</span>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {outdated.map(item => (
              <div key={item.serverId} className="px-4 py-3">
                <p className="font-medium text-gray-800 text-sm mb-2">{item.serverName}</p>
                {item.outdatedImages.map(img => (
                  <div key={img.imageName} className="flex items-center gap-2 text-xs mb-1">
                    <span className="font-mono text-gray-600">{img.imageName}</span>
                    <span className="bg-gray-100 text-gray-500 px-1.5 rounded font-mono">{img.currentTag}</span>
                    <span className="text-gray-400">→</span>
                    <span className="bg-green-100 text-green-700 px-1.5 rounded font-mono">{img.latestTag}</span>
                  </div>
                ))}
              </div>
            ))}
            {outdated.length === 0 && (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">모든 서버가 최신 상태입니다.</div>
            )}
          </div>
        </div>
      </div>

      {/* 버전 목록 모달 */}
      {tagsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-800">버전 목록</h3>
              <button onClick={() => { setTagsModal(null); setConfirmTag(null) }}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            <p className="text-xs text-gray-400 mb-4 font-mono">{tagsModal.imageName}</p>

            {fetchingTags ? (
              <p className="text-sm text-gray-400 text-center py-6">불러오는 중...</p>
            ) : availableTags.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">태그 정보가 없습니다.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">태그명</th>
                    <th className="text-left px-3 py-2">푸시 일시</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {availableTags.map((tag, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${tag.isLatestArtifact ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {tag.tagName}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-400">
                        {tag.pushedAt ? new Date(tag.pushedAt).toLocaleString('ko-KR') : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-right">
                        {tag.isLatestArtifact ? (
                          <span className="text-gray-300">현재 latest</span>
                        ) : confirmTag === tag.tagName ? (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => retargetMutation.mutate(tag.tagName)}
                              disabled={retargetMutation.isPending}
                              className="bg-orange-600 text-white px-2 py-0.5 rounded text-xs hover:bg-orange-500 disabled:opacity-50">
                              {retargetMutation.isPending ? '처리 중...' : '확인'}
                            </button>
                            <button onClick={() => setConfirmTag(null)}
                              className="border border-gray-300 text-gray-500 px-2 py-0.5 rounded text-xs">
                              취소
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmTag(tag.tagName)}
                            className="text-orange-600 hover:underline">
                            latest로 설정
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {retargetMutation.isError && (
              <p className="text-xs text-red-500 mt-3">
                오류: {(retargetMutation.error as { response?: { data?: string } })?.response?.data ?? '변경 실패. 서버 로그를 확인하세요.'}
              </p>
            )}
            {retargetMutation.isSuccess && (
              <p className="text-xs text-green-600 mt-3">latest 태그 변경 완료</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
